/**
 * App — top-level routing component.
 *
 * Phase 6 adds receptionist dashboard route.
 * Auth slice: protected routes, redirects, auth state initialization.
 */

import { lazy, Suspense, useEffect, useState } from 'react';

import { useAuthStore } from './lib/auth-store.js';

const LandingPage = lazy(() => import('./pages/LandingPage.tsx').then(({ LandingPage }) => ({ default: LandingPage })));
const LoginPage = lazy(() => import('./pages/LoginPage.tsx').then(({ LoginPage }) => ({ default: LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage.tsx').then(({ RegisterPage }) => ({ default: RegisterPage })));
const PasswordResetRequestPage = lazy(() => import('./pages/PasswordResetRequestPage.tsx').then(({ PasswordResetRequestPage }) => ({ default: PasswordResetRequestPage })));
const PasswordResetConfirmPage = lazy(() => import('./pages/PasswordResetConfirmPage.tsx').then(({ PasswordResetConfirmPage }) => ({ default: PasswordResetConfirmPage })));
const StaffInvitationAcceptPage = lazy(() => import('./pages/StaffInvitationAcceptPage.tsx').then(({ StaffInvitationAcceptPage }) => ({ default: StaffInvitationAcceptPage })));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage.tsx').then(({ EmailVerificationPage }) => ({ default: EmailVerificationPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage.tsx').then(({ DashboardPage }) => ({ default: DashboardPage })));
const ReceptionistDashboardPage = lazy(() => import('./pages/ReceptionistDashboardPage.tsx').then(({ ReceptionistDashboardPage }) => ({ default: ReceptionistDashboardPage })));
const PatientsPage = lazy(() => import('./pages/PatientsPage.tsx').then(({ PatientsPage }) => ({ default: PatientsPage })));
const AppointmentsPage = lazy(() => import('./pages/AppointmentsPage.tsx').then(({ AppointmentsPage }) => ({ default: AppointmentsPage })));
const ConversationsPage = lazy(() => import('./pages/ConversationsPage.tsx').then(({ ConversationsPage }) => ({ default: ConversationsPage })));
const StaffPage = lazy(() => import('./pages/StaffPage.tsx').then(({ StaffPage }) => ({ default: StaffPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage.tsx').then(({ SettingsPage }) => ({ default: SettingsPage })));
const ClinicOnboardingPage = lazy(() => import('./pages/ClinicOnboardingPage.tsx').then(({ ClinicOnboardingPage }) => ({ default: ClinicOnboardingPage })));
const ProvidersPage = lazy(() => import('./pages/ProvidersPage.tsx').then(({ ProvidersPage }) => ({ default: ProvidersPage })));
const AppointmentTypesPage = lazy(() => import('./pages/AppointmentTypesPage.tsx').then(({ AppointmentTypesPage }) => ({ default: AppointmentTypesPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage.tsx').then(({ PrivacyPage }) => ({ default: PrivacyPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage.tsx').then(({ NotificationsPage }) => ({ default: NotificationsPage })));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage.tsx').then(({ KnowledgeBasePage }) => ({ default: KnowledgeBasePage })));
const AiConfigPage = lazy(() => import('./pages/AiConfigPage.tsx').then(({ AiConfigPage }) => ({ default: AiConfigPage })));
const AutomationsPage = lazy(() => import('./pages/AutomationsPage.tsx').then(({ AutomationsPage }) => ({ default: AutomationsPage })));
const LunaPage = lazy(() => import('./pages/LunaPage.tsx').then(({ LunaPage }) => ({ default: LunaPage })));
const WhatsAppAgentPage = lazy(() => import('./pages/WhatsAppAgentPage.tsx').then(({ WhatsAppAgentPage }) => ({ default: WhatsAppAgentPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage.tsx').then(({ AnalyticsPage }) => ({ default: AnalyticsPage })));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage.tsx').then(({ AuditLogsPage }) => ({ default: AuditLogsPage })));
const RevenuePage = lazy(() => import('./pages/RevenuePage.tsx').then(({ RevenuePage }) => ({ default: RevenuePage })));
const CommunicationControlPlanePage = lazy(() => import('./pages/CommunicationControlPlanePage.tsx').then(({ CommunicationControlPlanePage }) => ({ default: CommunicationControlPlanePage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage.tsx').then(({ PrivacyPolicyPage }) => ({ default: PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage.tsx').then(({ TermsOfServicePage }) => ({ default: TermsOfServicePage })));
const DesignSystemPreviewPage = lazy(() => import('./pages/DesignSystemPreviewPage.tsx').then(({ DesignSystemPreviewPage }) => ({ default: DesignSystemPreviewPage })));
const PmsImportPage = lazy(() => import('./pages/PmsImportPage.tsx').then(({ PmsImportPage }) => ({ default: PmsImportPage })));
const CareJourneysPage = lazy(() => import('./pages/CareJourneysPage.tsx').then(({ CareJourneysPage }) => ({ default: CareJourneysPage })));
const BillingReturnPage = lazy(() => import('./pages/BillingReturnPage.tsx').then(({ BillingReturnPage }) => ({ default: BillingReturnPage })));

function getRoute(): string {
  const hash = window.location.hash.replace(/^#/, '');
  return hash.split('?')[0] || '/';
}

export function App() {
  const [route, setRoute] = useState(getRoute());
  const { isAuthenticated, initialize } = useAuthStore();
  const [authInitialized, setAuthInitialized] = useState(false);
  const isAuthRoute = route === '/login' || route === '/register' || route === '/forgot-password' || route === '/reset-password' || route === '/staff-invitation/accept' || route === '/verify-email';
  // One-time token flows must remain reachable even if this browser has another active session.
  const isSessionIndependentAuthRoute = route === '/staff-invitation/accept' || route === '/reset-password' || route === '/verify-email';
  const isProtectedRoute = route.startsWith('/app/');

  useEffect(() => {
    const handleHashChange = () => setRoute(getRoute());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    initialize();
    setAuthInitialized(true);
  }, [initialize]);

  useEffect(() => {
    if (!authInitialized) return;
    if (isAuthRoute && isAuthenticated && !isSessionIndependentAuthRoute) {
      window.location.hash = '/app/dashboard';
    } else if (isProtectedRoute && !isAuthenticated) {
      window.location.hash = '/login';
    }
  }, [authInitialized, isAuthRoute, isProtectedRoute, isAuthenticated]);

  if (!authInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if ((isAuthRoute && isAuthenticated && !isSessionIndependentAuthRoute) || (isProtectedRoute && !isAuthenticated)) {
    return null;
  }

  const page = (() => {
    switch (route) {
    case '/': return <LandingPage />;
    case '/login': return <LoginPage />;
    case '/register': return <RegisterPage />;
    case '/forgot-password': return <PasswordResetRequestPage />;
    case '/reset-password': return <PasswordResetConfirmPage />;
    case '/staff-invitation/accept': return <StaffInvitationAcceptPage />;
    case '/verify-email': return <EmailVerificationPage />;
    case '/privacy-policy': return <PrivacyPolicyPage />;
    case '/terms-of-service': return <TermsOfServicePage />;
    case '/__design-system': return import.meta.env.DEV ? <DesignSystemPreviewPage /> : <LandingPage />;
    case '/app/dashboard': return <DashboardPage />;
    case '/app/receptionist': return <ReceptionistDashboardPage />;
    case '/app/patients': return <PatientsPage />;
    case '/app/appointments': return <AppointmentsPage />;
    case '/app/conversations': return <ConversationsPage />;
    case '/app/staff': return <StaffPage />;
    case '/app/settings': return <SettingsPage />;
    case '/app/onboarding': return <ClinicOnboardingPage />;
    case '/app/providers': return <ProvidersPage />;
    case '/app/appointment-types': return <AppointmentTypesPage />;
    case '/app/privacy': return <PrivacyPage />;
    case '/app/notifications': return <NotificationsPage />;
    case '/app/knowledge-base': return <KnowledgeBasePage />;
    case '/app/ai-config': return <AiConfigPage />;
    case '/app/automations': return <AutomationsPage />;
    case '/app/luna': return <LunaPage />;
case '/app/whatsapp-agent': return <WhatsAppAgentPage />;
    case '/app/analytics': return <AnalyticsPage />;
    case '/app/audit-logs': return <AuditLogsPage />;
    case '/app/revenue': return <RevenuePage />;
    case '/app/communication': return <CommunicationControlPlanePage />;
    case '/app/pms-import': return <PmsImportPage />;
    case '/app/care-journeys': return <CareJourneysPage />;
    case '/app/billing/return': return <BillingReturnPage />;
      default:
        if (isProtectedRoute && isAuthenticated) return <DashboardPage />;
        return <LandingPage />;
    }
  })();

  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center" role="status" aria-label="Loading"><div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent" /></div>}>
      {page}
    </Suspense>
  );
}
